import pluralize from 'pluralize'
import { RoleType } from '../../models/Role'
import { seatTypeLabels } from './roleChangeSeatImpact'
import { type SeatTypeShortage } from './useRequestedSeatShortage'

const SEAT_TYPE_ORDER = [RoleType.SEAT, RoleType.SIDECAR]

const seats = (count: number) => pluralize('seat', count)

const requests = (count: number) => pluralize('request', count)

const bothTypes = (parts: string[]) => parts.join(' and ')

const countedTypes = (types: SeatTypeShortage[], count: (type: SeatTypeShortage) => number) =>
  bothTypes(types.map((type) => `${count(type)} ${seatTypeLabels[type.seatType]}`))

const total = (types: SeatTypeShortage[], count: (type: SeatTypeShortage) => number) => types.reduce((sum, type) => sum + count(type), 0)

export const requestedSeatsAlertCopy = (types: SeatTypeShortage[]): string | null => {
  const ordered = SEAT_TYPE_ORDER.map((seatType) => types.find((type) => type.seatType === seatType)).filter((type): type is SeatTypeShortage => !!type)
  const short = ordered.filter((type) => type.shortage > 0)

  if (short.length === 0) {
    return null
  }

  const missing = total(short, (type) => type.shortage)
  const withSeats = ordered.filter((type) => type.available > 0 && type.requested > 0)

  if (withSeats.length === 0) {
    const requested = total(short, (type) => type.requested)
    if (short.length === 1) {
      const label = seatTypeLabels[short[0].seatType]
      const needed = requested === 1 ? `an ${label} seat` : `${label} ${seats(requested)}`
      return `${requested} ${requests(requested)} ${requested === 1 ? 'needs' : 'need'} ${needed}.`
    }
    return `${requested} ${requests(requested)} need ${countedTypes(short, (type) => type.shortage)} ${seats(missing)}.`
  }

  const available = total(withSeats, (type) => type.available)
  const have = `You have ${bothTypes(withSeats.map((type) => `${type.available} available ${seatTypeLabels[type.seatType]}`))} ${seats(available)}.`

  if (withSeats.length === 1 && short.length === 1 && withSeats[0].seatType === short[0].seatType) {
    return `${have} Buy ${short[0].shortage} more to cover all ${short[0].requested} ${requests(short[0].requested)}.`
  }

  return `${have} Buy ${countedTypes(short, (type) => type.shortage)} ${seats(missing)} to cover all.`
}
